import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { AssignablePerson, BoardColumn } from '../lib/types';
import { WorkItemDrawer } from '../pages/WorkItemDrawer';

/**
 * A task opened in place, from a list that is not a board.
 *
 * `WorkItemDrawer` needs its project's context — the workflow columns, the sprints and who may
 * be assigned — because it lets you change all three. A board has those already; Priorities,
 * My work and search do not, and they span several projects at once, so the context differs
 * per row. That is why those lists used to NAVIGATE to the board instead: it was the only
 * place the drawer could be rendered.
 *
 * This fetches the context for one project and renders the drawer over whatever you were
 * looking at, so a glance at a task costs you your place in the list no longer.
 *
 * Two requests, on open only. They are the same two the board makes, so nothing new is being
 * asked of the API.
 */
export function TaskDrawer({
  itemId,
  projectId,
  onClose,
  onChanged,
}: {
  itemId: string;
  projectId: string;
  onClose: () => void;
  /** Called after an edit, so the list behind can refresh itself. */
  onChanged?: () => void | Promise<void>;
}) {
  const [context, setContext] = useState<{
    companyId: string;
    columns: BoardColumn[];
    people: AssignablePerson[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const [project, people] = await Promise.all([
          api.get<{
            project: { companyId: string };
            statuses: BoardColumn[];
          }>(`/projects/${projectId}`),
          api.get<AssignablePerson[]>(`/projects/${projectId}/assignable`),
        ]);
        if (!live) return;
        setContext({
          companyId: project.project.companyId,
          columns: project.statuses,
          people,
        });
      } catch (err) {
        if (live) setError(err instanceof ApiError ? err.message : 'Could not open that task');
      }
    })();
    return () => {
      live = false;
    };
  }, [projectId]);

  if (error) {
    return (
      <div className="drawer-backdrop" onClick={onClose}>
        <div className="drawer" onClick={(event) => event.stopPropagation()}>
          <div className="drawer-body">
            <div className="callout">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  // Nothing until the context lands — a drawer with empty pickers would let somebody set a
  // status the project does not have.
  if (!context) return null;

  return (
    <WorkItemDrawer
      itemId={itemId}
      companyId={context.companyId}
      people={context.people}
      columns={context.columns}
      onClose={onClose}
      onChanged={() => onChanged?.()}
      onDeleted={() => {
        onClose();
        return onChanged?.();
      }}
    />
  );
}
